import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { STORIES, getStoryBySlug } from '../data/storiesData';

export default function StoryDetail() {
  const { slug } = useParams();
  const story = getStoryBySlug(slug || '');

  if (!story) {
    return <Navigate to="/stories" replace />;
  }

  const moreStories = STORIES.filter((item) => item.slug !== story.slug).slice(0, 4);
  const isFounderStory = story.slug === 'founder';

  return (
    <div className="story-page">
      <section className="story-hero">
        <div className="story-hero-bg" style={{ backgroundImage: `url('${story.heroImage}')` }}></div>
        <div className="story-hero-overlay"></div>
        <div className="story-hero-content">
          <p className="story-breadcrumb">
            <Link to="/stories">Stories</Link>
            <span>/</span>
            <span>{story.navLabel}</span>
          </p>
          <h1>{story.title}</h1>
          <p>{story.subtitle}</p>
        </div>
      </section>

      <section className="story-intro-wrap">
        <p className="story-intro">{story.intro}</p>
        <div className="story-stats-grid">
          {story.stats.map((stat) => (
            <div key={stat.label} className="story-stat-card">
              <span>{stat.value}</span>
              <p>{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {isFounderStory && (
        <section className="story-founder-wrap">
          <div className="story-founder-photo-card">
            {story.founderPortrait ? (
              <img src={story.founderPortrait} alt={story.founderName || 'Founder'} className="story-founder-photo" />
            ) : (
              <div className="story-founder-placeholder">
                <span>Founder Portrait</span>
                <p>Add your image path in src/config/images.js under founderPortrait</p>
              </div>
            )}
          </div>
          <div className="story-founder-copy">
            <span className="story-kicker">Founder Spotlight</span>
            <h2>{story.founderName || 'Founder'}</h2>
            <h3>{story.founderRole || 'Founder, Kaaleen Haus'}</h3>
            <p>{story.founderNote}</p>
            <p className="story-founder-line">{story.founderExperienceLine}</p>
          </div>
        </section>
      )}

      <section className="story-sections-wrap">
        {story.sections.map((section, idx) => (
          <article key={section.title} className={`story-section ${idx % 2 === 0 ? 'left-media' : 'right-media'}`}>
            <div className="story-section-media" style={{ backgroundImage: `url('${section.image}')` }}></div>
            <div className="story-section-copy">
              <h2>{section.title}</h2>
              <p>{section.body}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="story-quote-wrap">
        <blockquote>"{story.quote}"</blockquote>
        <cite>{story.quoteBy}</cite>
      </section>

      <section className="story-final-image-wrap">
        <div className="story-final-image" style={{ backgroundImage: `url('${story.finalImage}')` }}></div>
      </section>

      <section className="story-more-wrap">
        <h3>More Stories</h3>
        <div className="story-more-grid">
          {moreStories.map((item) => (
            <Link key={item.slug} to={`/stories/${item.slug}`} className="story-more-card">
              <div className="story-more-image" style={{ backgroundImage: `url('${item.cardImage}')` }}></div>
              <div className="story-more-meta">
                <h4>{item.navLabel}</h4>
                <p>{item.navSub}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <style>{storyDetailStyles}</style>
    </div>
  );
}

const storyDetailStyles = `
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700&family=Playfair+Display:wght@400;500;600;700&display=swap');

.story-page {
  font-family: 'Manrope', sans-serif;
  background:
    radial-gradient(circle at 15% 8%, rgba(197, 160, 99, 0.12) 0%, rgba(197, 160, 99, 0) 32%),
    linear-gradient(180deg, #f4f2ed 0%, #f8f6f1 100%);
  color: #1b1c2f;
}

.story-hero {
  min-height: 70vh;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
}

.story-hero-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  transform: scale(1.03);
}

.story-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(12, 16, 32, 0.3) 0%, rgba(12, 16, 32, 0.8) 100%);
}

.story-hero-content {
  position: relative;
  z-index: 1;
  max-width: 1160px;
  width: 100%;
  margin: 0 auto;
  padding: 0 24px 46px;
}

.story-breadcrumb {
  margin: 0 0 10px;
  font-size: 11px;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: rgba(234, 222, 205, 0.95);
}

.story-breadcrumb a {
  color: inherit;
  text-decoration: none;
}

.story-breadcrumb span {
  margin: 0 6px;
}

.story-hero-content h1 {
  margin: 0;
  max-width: 840px;
  font-family: 'Playfair Display', serif;
  font-size: clamp(2rem, 4.5vw, 3.5rem);
  line-height: 1.08;
  color: #ffffff;
}

.story-hero-content p:last-child {
  margin: 14px 0 0;
  max-width: 760px;
  color: rgba(255, 255, 255, 0.86);
  line-height: 1.75;
}

.story-intro-wrap,
.story-founder-wrap,
.story-sections-wrap,
.story-quote-wrap,
.story-final-image-wrap,
.story-more-wrap {
  max-width: 1160px;
  margin: 0 auto;
  padding-left: 24px;
  padding-right: 24px;
}

.story-intro-wrap {
  padding-top: 46px;
}

.story-intro {
  margin: 0 auto;
  max-width: 860px;
  text-align: center;
  font-size: 1.03rem;
  color: #3b3f58;
  line-height: 1.85;
}

.story-stats-grid {
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.story-stat-card {
  border: 1px solid #e5dbcb;
  border-radius: 12px;
  background: #fffcf7;
  text-align: center;
  padding: 16px 10px;
}

.story-stat-card span {
  display: block;
  font-family: 'Playfair Display', serif;
  font-size: 1.6rem;
  color: #9b7235;
}

.story-stat-card p {
  margin: 5px 0 0;
  font-size: 11px;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: #595d74;
}

.story-founder-wrap {
  margin-top: 34px;
  padding-top: 8px;
  display: grid;
  grid-template-columns: 0.95fr 1.05fr;
  gap: 20px;
}

.story-founder-photo-card {
  border-radius: 14px;
  border: 1px solid #e5dbcb;
  min-height: 360px;
  overflow: hidden;
  background: #ffffff;
}

.story-founder-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.story-founder-placeholder {
  width: 100%;
  height: 100%;
  min-height: 360px;
  background:
    radial-gradient(circle at 25% 20%, rgba(197, 160, 99, 0.22) 0%, rgba(197, 160, 99, 0.03) 36%),
    linear-gradient(145deg, #131a2f 0%, #2a3148 100%);
  color: #f7efe2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
}

.story-founder-placeholder span {
  font-family: 'Playfair Display', serif;
  font-size: 1.7rem;
  margin-bottom: 10px;
}

.story-founder-placeholder p {
  margin: 0;
  font-size: 12px;
  max-width: 280px;
  line-height: 1.6;
  color: rgba(247, 239, 226, 0.82);
}

.story-founder-copy {
  border: 1px solid #e5dbcb;
  border-radius: 14px;
  background: #ffffff;
  padding: 24px 24px 22px;
}

.story-kicker {
  display: inline-block;
  font-size: 10px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #af8240;
  margin-bottom: 10px;
  font-weight: 700;
}

.story-founder-copy h2 {
  margin: 0;
  font-family: 'Playfair Display', serif;
  font-size: 2.1rem;
  line-height: 1.1;
  color: #171a2d;
}

.story-founder-copy h3 {
  margin: 8px 0 12px;
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7f6482;
}

.story-founder-copy p {
  margin: 0;
  color: #434866;
  line-height: 1.85;
}

.story-founder-line {
  margin-top: 14px !important;
  padding-top: 12px;
  border-top: 1px solid #eee4d6;
  color: #1e2238 !important;
  font-weight: 600;
}

.story-sections-wrap {
  padding-top: 34px;
  display: grid;
  gap: 22px;
}

.story-section {
  border: 1px solid #e5dbcb;
  border-radius: 14px;
  overflow: hidden;
  background: #ffffff;
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.story-section.right-media .story-section-media {
  order: 2;
}

.story-section-media {
  min-height: 320px;
  background-size: cover;
  background-position: center;
}

.story-section-copy {
  padding: 28px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.story-section-copy h2 {
  margin: 0 0 10px;
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.5rem, 2.8vw, 2.2rem);
  line-height: 1.15;
}

.story-section-copy p {
  margin: 0;
  color: #4a4f6a;
  line-height: 1.85;
}

.story-quote-wrap {
  padding-top: 34px;
  text-align: center;
}

.story-quote-wrap blockquote {
  margin: 0 auto;
  max-width: 880px;
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.35rem, 3.1vw, 2.2rem);
  font-style: italic;
  line-height: 1.45;
  color: #1d2038;
}

.story-quote-wrap cite {
  display: inline-block;
  margin-top: 12px;
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #8f7448;
  font-style: normal;
  font-weight: 700;
}

.story-final-image-wrap {
  padding-top: 30px;
}

.story-final-image {
  height: 420px;
  border: 1px solid #e5dbcb;
  border-radius: 14px;
  background-size: cover;
  background-position: center;
}

.story-more-wrap {
  padding-top: 42px;
  padding-bottom: 74px;
}

.story-more-wrap h3 {
  margin: 0 0 18px;
  text-align: center;
  font-family: 'Playfair Display', serif;
  font-size: 2rem;
}

.story-more-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.story-more-card {
  border: 1px solid #e5dbcb;
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  background: #fff;
  transition: transform 0.22s ease, box-shadow 0.22s ease;
}

.story-more-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 26px rgba(26, 31, 48, 0.12);
}

.story-more-image {
  height: 172px;
  background-size: cover;
  background-position: center;
}

.story-more-meta {
  padding: 12px;
}

.story-more-meta h4 {
  margin: 0 0 4px;
  font-size: 14px;
  color: #171a2d;
}

.story-more-meta p {
  margin: 0;
  font-size: 12px;
  color: #686c83;
}

@media (max-width: 1000px) {
  .story-stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .story-founder-wrap,
  .story-section {
    grid-template-columns: 1fr;
  }

  .story-section.right-media .story-section-media {
    order: 0;
  }

  .story-more-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .story-hero {
    min-height: 62vh;
  }

  .story-hero-content,
  .story-intro-wrap,
  .story-founder-wrap,
  .story-sections-wrap,
  .story-quote-wrap,
  .story-final-image-wrap,
  .story-more-wrap {
    padding-left: 16px;
    padding-right: 16px;
  }

  .story-stats-grid,
  .story-more-grid {
    grid-template-columns: 1fr;
  }

  .story-final-image {
    height: 260px;
  }
}
`;
